import { WidgetId } from 'types/idTypes';
import { WidgetsMap } from 'types/infographTypes';
import { getWidgetRect, isWidgetContained } from 'widgets/WidgetLayout.helpers';
import { WidgetRect } from 'widgets/WidgetLayout.types';

/**
 * Finds widgets placed on top of a base widget based on their layer order and overlap percentage.
 *
 * @param baseWidgetId - The ID of the base widget.
 * @param layerOrder - The order of the widgets in the layer.
 * @param widgets - A map of all the widgets.
 * @param overlapPercentage - The minimum overlap percentage required for a widget to be considered on top.
 * @param onlyDirectDescendants - Indicates whether only direct descendants should be considered.
 *
 * @returns An array of widgets that are on top of the base widget.
 */
export const findWidgetsOnTopOf = (
  baseWidgetId: WidgetId,
  layerOrder: WidgetId[],
  widgets: WidgetsMap,
  overlapPercentage: number = 98,
  onlyDirectDescendants: boolean = true,
) => {
  const members = [];
  const baseRect = getWidgetRect(widgets[baseWidgetId]);

  for (let i = 0; i < layerOrder.length; i++) {
    const layerWidgetId = layerOrder[i];
    if (layerWidgetId === baseWidgetId) continue;

    const layerRect = getWidgetRect(widgets[layerWidgetId]);
    const isContained = isWidgetContained(baseRect, layerRect, overlapPercentage);

    if (isContained) {
      if (onlyDirectDescendants) {
        // loop backwards from the layerPosition and see if its fully covered by another widget
        let fullyCovered = false;
        for (let j = i - 1; j >= 0; j--) {
          const otherWidgetId = layerOrder[j];
          const otherWidget = widgets[otherWidgetId];

          if (otherWidgetId === baseWidgetId) break;

          if (isWidgetContained(getWidgetRect(otherWidget), layerRect, overlapPercentage)) {
            fullyCovered = true;
            break;
          }
        }

        if (fullyCovered) continue;
      }

      members.push({ id: layerWidgetId, rect: layerRect });
    }
  }

  return members;
};

// temporary debug helper to visualize the widget groups
export const drawDebugRect = (rect: WidgetRect, color: string, appendEl: HTMLElement | null) => {
  if (!appendEl) return;

  const div = document.createElement('div');
  div.className = 'debug-rect';
  div.style.position = 'absolute';
  div.style.top = rect.topPx + 'px';
  div.style.left = rect.leftPx + 'px';
  div.style.width = rect.widthPx + 'px';
  div.style.height = rect.heightPx + 'px';
  div.style.border = '2px solid ' + color;
  div.style.pointerEvents = 'none';
  div.style.zIndex = '1000';
  appendEl.appendChild(div);
};

// temporary debug helper to remove all debug rects
export const removeDebugRects = () => {
  const existingRects = document.getElementsByClassName('debug-rect');
  for (const rect of existingRects) rect.remove();
};
